import { api, setUnauthorizedHandler } from '@services/api';
import { appRoutes } from '@routes/appRoutes';
import { type AxiosError } from 'axios';
import { createContext, type ReactNode, useRef, useCallback, useEffect, useMemo, useState } from 'react';
import { type LogoutReason, broadcastLogout, persistLogoutReason, subscribeToLogout } from './authSession';

type User = {
  id: string;
  email: string;
  name: string;
  isAdmin?: boolean;
  role?: 'leader' | 'normal';
  accessLevel?: 'admin' | 'leader' | 'normal';
  view: string[];
  create: string[];
  upgrade: string[];
  del: string[];
};

type SignInCredentials = {
  email: string;
  password: string;
};

type AuthErrorResponse = {
  code?: string;
  message?: string | string[];
  error?: unknown;
};

type AuthContextData = {
  signIn: (credentials: SignInCredentials) => Promise<void>;
  signOut: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  user?: User;
};

type AuthProviderProps = {
  children: ReactNode;
};

export const AuthContext = createContext({} as AuthContextData);

const PUBLIC_AUTH_ENTRY_PATHS = new Set([appRoutes.auth.login, appRoutes.auth.forgotPassword, appRoutes.auth.resetPassword, '/forgot-password']);

export function shouldRedirectUnauthenticatedToLogin(pathname: string): boolean {
  const normalizedPath = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return !PUBLIC_AUTH_ENTRY_PATHS.has(normalizedPath);
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User>();
  const [loading, setLoading] = useState<boolean>(true);
  const logoutInFlightRef = useRef(false);
  const isAuthenticated = !!user?.id;

  const clearSession = useCallback(
    ({
      reason,
      redirectToLogin = false,
      broadcast = false,
    }: {
      reason?: LogoutReason;
      redirectToLogin?: boolean;
      broadcast?: boolean;
    } = {}) => {
      persistLogoutReason(reason);
      if (broadcast && reason) {
        broadcastLogout(reason);
      }

      setUser(undefined);
      setLoading(false);
      logoutInFlightRef.current = true;

      if (redirectToLogin && shouldRedirectUnauthenticatedToLogin(window.location.pathname)) {
        window.location.replace(appRoutes.auth.login);
      }
    },
    []
  );

  const resolveLogoutReason = useCallback((error?: AxiosError<AuthErrorResponse>): LogoutReason => {
    const code = error?.response?.data.code;
    if (code === 'SESSION_REVOKED') {
      return 'session_replaced';
    }
    if (code === 'SESSION_RENEW_REQUIRED') {
      return 'session_renew_required';
    }
    return 'unauthorized';
  }, []);

  const finalizeSession = useCallback(
    ({
      reason,
      redirectToLogin = true,
      broadcast = true,
      notifyServer = true,
    }: {
      reason: LogoutReason;
      redirectToLogin?: boolean;
      broadcast?: boolean;
      notifyServer?: boolean;
    }) => {
      if (logoutInFlightRef.current) {
        return;
      }

      logoutInFlightRef.current = true;

      if (notifyServer) {
        api.post('/auth/logout').catch(() => void 0);
      }

      clearSession({ reason, redirectToLogin, broadcast });
    },
    [clearSession]
  );

  useEffect(() => {
    return subscribeToLogout((reason) => {
      clearSession({
        reason,
        redirectToLogin: true,
        broadcast: false,
      });
    });
  }, [clearSession]);

  useEffect(() => {
    logoutInFlightRef.current = false;
    api
      .get('/auth/me')
      .then((response) => {
        persistLogoutReason(undefined);
        logoutInFlightRef.current = false;
        setUser(response.data);
      })
      .catch((error: AxiosError<AuthErrorResponse>) => {
        if (error.response?.status === 401) {
          finalizeSession({
            reason: resolveLogoutReason(error),
            redirectToLogin: window.location.pathname !== appRoutes.auth.login,
            broadcast: true,
            notifyServer: true,
          });
          return;
        }

        setUser(undefined);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [finalizeSession, resolveLogoutReason]);

  useEffect(() => {
    if (!isAuthenticated) {
      setUnauthorizedHandler(undefined);
      return () => {
        setUnauthorizedHandler(undefined);
      };
    }

    const handleUnauthorized = (error: AxiosError<AuthErrorResponse>) => {
      finalizeSession({
        reason: resolveLogoutReason(error),
        redirectToLogin: true,
        broadcast: true,
        notifyServer: true,
      });
    };

    setUnauthorizedHandler(handleUnauthorized);
    return () => {
      setUnauthorizedHandler(undefined);
    };
  }, [finalizeSession, isAuthenticated, resolveLogoutReason]);

  const signIn = async ({ email, password }: SignInCredentials) => {
    logoutInFlightRef.current = false;
    const response = await api.post('/auth/login', {
      email: email.trim().toLowerCase(),
      password,
    });
    const responseUser = response.data?.user || response.data || {};
    const { view = [], create = [], del: deleteRole = [], upgrade = [], name, id, isAdmin = false, role = 'normal', accessLevel, email: responseEmail } = responseUser;

    setUser({
      email: responseEmail || email.trim().toLowerCase(),
      name,
      isAdmin,
      role,
      accessLevel: accessLevel || (isAdmin ? 'admin' : role),
      view,
      create,
      del: deleteRole,
      upgrade,
      id,
    });

    persistLogoutReason(undefined);
    window.location.replace(appRoutes.dashboard);
  };

  const handleSignOut = useCallback(() => {
    finalizeSession({
      reason: 'manual',
      redirectToLogin: true,
      broadcast: true,
      notifyServer: true,
    });
  }, [finalizeSession]);

  const value = useMemo(
    () => ({
      signIn,
      isAuthenticated,
      isLoading: loading,
      user,
      signOut: handleSignOut,
    }),
    [handleSignOut, isAuthenticated, loading, user]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
